import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, CheckCircle, Loader2, Check, X } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { t, language } = useLanguage();
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Password validation states
  const hasMinLength = newPassword.length >= 8;
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasLowercase = /[a-z]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
  const isValidPassword = hasMinLength && hasUppercase && hasLowercase && hasNumber && passwordsMatch;

  // Translations
  const tr = {
    en: {
      missingToken: 'Missing reset token',
      pageTitle: 'Reset Password',
      pageSubtitle: 'Enter your new password below',
      newPassword: 'New password',
      newPasswordPlaceholder: 'Enter your new password',
      confirmPassword: 'Confirm password',
      confirmPasswordPlaceholder: 'Repeat password',
      minLength: 'At least 8 characters',
      uppercase: 'One uppercase letter',
      lowercase: 'One lowercase letter',
      number: 'One number',
      passwordsMatch: 'Passwords match',
      resetButton: 'Reset password',
      resetting: 'Resetting...',
      backToLogin: 'Back to login',
      successTitle: 'Password reset!',
      successMessage: 'Your password has been successfully reset. You will be redirected to the login page...',
      success: 'Password reset successfully!',
      error: 'Error resetting password',
      errorMinLength: 'Password must be at least 8 characters',
      errorUppercase: 'Password must contain at least one uppercase letter',
      errorLowercase: 'Password must contain at least one lowercase letter',
      errorNumber: 'Password must contain at least one number',
      errorMatch: 'Passwords do not match'
    },
    fr: {
      missingToken: 'Token de réinitialisation manquant',
      pageTitle: 'Réinitialiser le mot de passe',
      pageSubtitle: 'Entrez votre nouveau mot de passe ci-dessous',
      newPassword: 'Nouveau mot de passe',
      newPasswordPlaceholder: 'Entrez votre nouveau mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      confirmPasswordPlaceholder: 'Répétez le mot de passe',
      minLength: 'Au moins 8 caractères',
      uppercase: 'Une lettre majuscule',
      lowercase: 'Une lettre minuscule',
      number: 'Un chiffre',
      passwordsMatch: 'Les mots de passe correspondent',
      resetButton: 'Réinitialiser le mot de passe',
      resetting: 'Réinitialisation...',
      backToLogin: 'Retour à la connexion',
      successTitle: 'Mot de passe réinitialisé!',
      successMessage: 'Votre mot de passe a été réinitialisé avec succès. Vous allez être redirigé vers la page de connexion...',
      success: 'Mot de passe réinitialisé avec succès!',
      error: 'Erreur lors de la réinitialisation',
      errorMinLength: 'Le mot de passe doit contenir au moins 8 caractères',
      errorUppercase: 'Le mot de passe doit contenir au moins une majuscule',
      errorLowercase: 'Le mot de passe doit contenir au moins une minuscule',
      errorNumber: 'Le mot de passe doit contenir au moins un chiffre',
      errorMatch: 'Les mots de passe ne correspondent pas'
    }
  };

  const txt = tr[language] || tr.fr;

  useEffect(() => {
    if (!token) {
      toast.error(txt.missingToken);
      navigate("/auth");
    }
  }, [token, navigate, txt.missingToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasMinLength) {
      toast.error(txt.errorMinLength);
      return;
    }
    if (!hasUppercase) {
      toast.error(txt.errorUppercase);
      return;
    }
    if (!hasLowercase) {
      toast.error(txt.errorLowercase);
      return;
    }
    if (!hasNumber) {
      toast.error(txt.errorNumber);
      return;
    }
    if (!passwordsMatch) {
      toast.error(txt.errorMatch);
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: newPassword
      });
      
      setIsSuccess(true);
      toast.success(txt.success);
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/auth");
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || txt.error);
    }
    setIsLoading(false);
  };

  const PasswordRequirement = ({ met, text }) => (
    <div className={`flex items-center gap-2 text-sm ${met ? 'text-green-600' : 'text-gray-400'}`}>
      {met ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
      {text}
    </div>
  );

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-brand-50 to-cyan-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <CardTitle className="text-2xl">{txt.successTitle}</CardTitle>
            <CardDescription>{txt.successMessage}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-brand-50 to-cyan-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-brand-600" />
          </div>
          <CardTitle className="text-2xl">{txt.pageTitle}</CardTitle>
          <CardDescription>{txt.pageSubtitle}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">{txt.newPassword}</Label>
              <Input
                id="new-password"
                type="password"
                placeholder={txt.newPasswordPlaceholder}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={isLoading}
              />
              
              {/* Password requirements */}
              {newPassword.length > 0 && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-1">
                  <PasswordRequirement met={hasMinLength} text={txt.minLength} />
                  <PasswordRequirement met={hasUppercase} text={txt.uppercase} />
                  <PasswordRequirement met={hasLowercase} text={txt.lowercase} />
                  <PasswordRequirement met={hasNumber} text={txt.number} />
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-password">{txt.confirmPassword}</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder={txt.confirmPasswordPlaceholder}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
              />
              {confirmPassword.length > 0 && (
                <PasswordRequirement met={passwordsMatch} text={txt.passwordsMatch} />
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-brand-600 hover:bg-brand-700"
              disabled={isLoading || !isValidPassword}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {txt.resetting}
                </>
              ) : (
                txt.resetButton
              )}
            </Button>

            <div className="text-center text-sm text-gray-600">
              <button
                type="button"
                onClick={() => navigate("/auth")}
                className="text-brand-600 hover:underline"
              >
                {txt.backToLogin}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
